import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Upload, Copy, Trash2, Search, Folder, X, Check } from "lucide-react";

const DEFAULT_FOLDERS = ["products", "categories", "seo", "homepage", "banners", "blog", "logo", "favicon"];

export function MediaLibrary({ mode = "page", folder: defaultFolder = "products", onSelect, onClose }) {
  const [items, setItems] = useState([]);
  const [folder, setFolder] = useState(defaultFolder);
  const [folders, setFolders] = useState(DEFAULT_FOLDERS);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const reload = async () => {
    const qs = new URLSearchParams();
    if (folder) qs.set("folder", folder);
    if (q) qs.set("q", q);
    const { data } = await api.get(`/admin/media?${qs.toString()}`);
    setItems(data);
  };

  useEffect(() => { reload(); }, [folder]);
  useEffect(() => { api.get("/admin/media/folders").then((r) => setFolders(r.data)).catch(() => {}); }, []);

  const upload = async (files) => {
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await api.post(`/upload?folder=${encodeURIComponent(folder)}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} image(s) uploaded`);
      reload();
    } catch (e) { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const copyUrl = (item) => {
    const full = `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
    navigator.clipboard.writeText(full);
    toast.success("URL copied");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    await api.delete(`/admin/media/${id}`);
    reload();
  };

  const fullUrl = (item) => `${process.env.REACT_APP_BACKEND_URL}${item.url}`;

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) upload(files);
  };

  const isModal = mode === "picker";
  const Wrapper = isModal ? "div" : React.Fragment;
  const wrapperProps = isModal ? { className: "fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-6", onClick: onClose } : {};

  const Body = (
    <div data-testid="media-library" className={isModal ? "bg-white max-w-6xl w-full max-h-[88vh] overflow-y-auto p-6" : ""} onClick={isModal ? (e) => e.stopPropagation() : undefined}>
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl mb-1">{isModal ? "Görsel Seç" : "Medya Kütüphanesi"}</h1>
          <p className="text-xs text-[#52525B]">{items.length} görsel · klasör: {folder}</p>
        </div>
        {isModal && <button onClick={onClose} className="p-2 hover:bg-[#FAF8F3]"><X size={20} /></button>}
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <select data-testid="media-folder" value={folder} onChange={(e) => setFolder(e.target.value)}
          className="border border-border bg-white px-3 py-2 text-sm">
          {folders.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <div className="flex items-center gap-2 border border-border bg-white px-3 py-2 flex-1 min-w-[200px]">
          <Search size={14} className="text-[#71717A]" />
          <input data-testid="media-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && reload()}
            placeholder="Ara..." className="text-sm focus:outline-none bg-transparent flex-1" />
        </div>
        <button data-testid="media-upload-btn" onClick={() => inputRef.current?.click()} className="bg-[#222] hover:bg-[#C8A96A] text-white px-5 py-2 text-xs uppercase tracking-[0.2em] transition-colors inline-flex items-center gap-2">
          <Upload size={14} /> Yükle
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => e.target.files && upload(Array.from(e.target.files))} />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="media-drop-zone"
        className={`border-2 border-dashed p-4 mb-4 text-center text-xs transition-colors ${drag ? "border-[#C8A96A] bg-[#C8A96A]/5" : "border-border text-[#71717A]"}`}
      >
        {uploading ? "Yükleniyor..." : "Görselleri buraya sürükleyin veya yukarıdaki butonu kullanın"}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-16 text-sm text-[#71717A]">Bu klasör boş</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
          {items.map((it) => (
            <div key={it.id} data-testid={`media-item-${it.id}`} className="border border-border bg-white group">
              <div className="aspect-square bg-[#FAF8F3] overflow-hidden relative">
                <img src={fullUrl(it)} alt={it.filename} className="w-full h-full object-cover" loading="lazy" />
                {isModal && (
                  <button data-testid={`media-select-${it.id}`} onClick={() => { onSelect(fullUrl(it)); onClose?.(); }}
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 text-white flex items-center justify-center transition-opacity">
                    <Check size={28} />
                  </button>
                )}
              </div>
              <div className="p-2 text-[10px]">
                <p className="truncate font-medium" title={it.filename}>{it.filename}</p>
                <p className="text-[#71717A]">{new Date(it.created_at).toLocaleDateString("tr-TR")}</p>
                <div className="flex justify-between mt-1.5">
                  <button onClick={() => copyUrl(it)} className="text-[#C8A96A] hover:text-[#A6864A]" title="Copy URL"><Copy size={12} /></button>
                  <button onClick={() => del(it.id)} className="text-[#EF4444] hover:opacity-80" title="Delete"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (isModal) return <Wrapper {...wrapperProps}>{Body}</Wrapper>;
  return Body;
}

export function ImagePicker({ value, onChange, folder = "products", testid = "image-picker" }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="" className="w-16 h-16 object-cover bg-[#FAF8F3] border border-border" />
        ) : (
          <div className="w-16 h-16 bg-[#FAF8F3] border border-dashed border-border flex items-center justify-center text-[#71717A]"><Folder size={20} /></div>
        )}
        <div className="flex-1 flex flex-col gap-1">
          <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL veya kütüphaneden seç"
            className="w-full border-b border-[#222] bg-transparent py-1.5 text-sm focus:outline-none focus:border-[#C8A96A]" />
          <button type="button" data-testid={`${testid}-open`} onClick={() => setOpen(true)}
            className="text-[10px] uppercase tracking-[0.2em] text-[#C8A96A] hover:text-[#A6864A] self-start">
            Kütüphaneden Seç
          </button>
        </div>
      </div>
      {open && <MediaLibrary mode="picker" folder={folder} onSelect={(url) => onChange(url)} onClose={() => setOpen(false)} />}
    </div>
  );
}
